import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";

declare const Android: AndroidInterface;

enum GeolocationErrorType {
  NO_PERMISSION = "NO_PERMISSION",
  LOCATION_DISABLED = "LOCATION_DISABLED",
  TIMEOUT = "TIMEOUT",
  POSITION_UNAVAILABLE = "POSITION_UNAVAILABLE",
  NOT_ANDROID = "NOT_ANDROID",
}

enum GeolocationProvider {
  FUSED = "FUSED",
  NETWORK = "NETWORK",
  GPS = "GPS",
}

enum PermissionStatus {
  GRANTED = "GRANTED",
  DENIED = "DENIED",
  NOT_DETERMINED = "NOT_DETERMINED",
}

interface GeolocationSuccessResponse {
  success: true;
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
  isFromLastKnown: boolean;
  provider: GeolocationProvider;
  permissionStatus: PermissionStatus.GRANTED;
  warning?: string;
}

interface GeolocationErrorResponse {
  success: false;
  error: string;
  errorType: GeolocationErrorType;
  permissionStatus: PermissionStatus;
  requiresSettingsChange: boolean;
}

type GeolocationResponse =
  | GeolocationSuccessResponse
  | GeolocationErrorResponse;

declare global {
  interface Window {
    onNativeLocationReceived?: (data: GeolocationResponse) => void;
  }
}

export function useGeolocation(): Promise<GeolocationSuccessResponse> {
  return new Promise((resolve, reject) => {
    if (!isAndroid()) {
      reject({
        success: false,
        error: "Not an Android device",
        errorType: GeolocationErrorType.NOT_ANDROID,
        permissionStatus: PermissionStatus.NOT_DETERMINED,
        requiresSettingsChange: false,
      });
      return;
    }

    if (typeof Android.requestNativeLocation !== "function") {
      reject({
        success: false,
        error: "Location services not available",
        errorType: GeolocationErrorType.LOCATION_DISABLED,
        permissionStatus: PermissionStatus.NOT_DETERMINED,
        requiresSettingsChange: true,
      });
      return;
    }

    window.onNativeLocationReceived = (data: GeolocationResponse) => {
      if (data.success) {
        resolve(data);
      } else {
        reject(data);
      }
      delete window.onNativeLocationReceived;
    };

    Android.requestNativeLocation();
  });
}

export { GeolocationErrorType, GeolocationProvider, PermissionStatus };
export type {
  GeolocationResponse,
  GeolocationSuccessResponse,
  GeolocationErrorResponse,
};
